import BusinessIcon from '@mui/icons-material/Business'
import { Alert, Avatar, Box, CircularProgress, Typography } from '@mui/material'
import React, { useEffect, useState } from 'react'
import { Navigate } from 'react-router-dom'
import { useRecoilValue } from 'recoil'

import { AppBarR, Wrapper } from '../components/common'
import { OfficeManagementList } from '../components/office'
import db from '../db/db'
import { sessionState } from '../state'
import Database from '../types/Database'
import { addRolesToOffices, OfficeWithRole } from '../utils/profileHelpers'

import routes from './routes'

/**
 * Offices - Office management page component
 *
 * Displays offices with management capabilities.
 * Shows office list with edit, view, and desk management options.
 * Similar to Profile page but focused on office management.
 */
const Offices: React.FC = () => {
  const session = useRecoilValue(sessionState)
  const { user, offices, isAuthenticating } = session

  const [officesWithRoles, setOfficesWithRoles] = useState<OfficeWithRole[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Fetch office managers and determine roles
  useEffect(() => {
    const fetchOfficeRoles = async () => {
      if (!user || !offices) {
        setLoading(false)
        return
      }

      try {
        setLoading(true)
        setError(null)

        // Fetch office managers for role determination
        const officeIds = offices.map(office => office.oid).filter(Boolean)
        let officeManagers: Database.OfficeManager[] = []

        if (officeIds.length > 0) {
          try {
            // Fetch all office managers for these offices
            officeManagers = await db.query({
              collection: 'officeManagers',
              where: ['officeId', 'in', officeIds]
            })

            // Also try to fetch office managers specifically for this user's email
            if (
              user.email &&
              typeof user.email === 'string' &&
              user.email.trim() !== ''
            ) {
              try {
                const userSpecificManagers = await db.query({
                  collection: 'officeManagers',
                  where: ['email', '==', user.email]
                })

                // Merge the results, avoiding duplicates
                const allManagers = [...officeManagers, ...userSpecificManagers]
                officeManagers = allManagers.filter(
                  (manager, index, self) =>
                    index === self.findIndex(m => m.id === manager.id)
                )
              } catch (emailQueryError) {
                console.warn(
                  'Could not fetch user-specific office managers:',
                  emailQueryError
                )
              }
            }
          } catch (queryError) {
            console.warn(
              'Could not fetch office managers, using empty array:',
              queryError
            )
            officeManagers = []
          }
        }

        // Add roles to offices
        const officesWithRoleData = addRolesToOffices(
          offices,
          user,
          officeManagers
        )
        setOfficesWithRoles(officesWithRoleData)
      } catch (err) {
        console.error('Error fetching office roles:', err)
        setError(
          'Failed to load office information. Please try refreshing the page.'
        )
      } finally {
        setLoading(false)
      }
    }

    fetchOfficeRoles()
  }, [user, offices])

  // Redirect unauthenticated users to sign-in page
  if (!isAuthenticating && !user) {
    return <Navigate to={routes.signin} replace />
  }

  // Show loading state during authentication
  if (isAuthenticating) {
    return (
      <>
        <AppBarR />
        <Wrapper>
          <Box
            display='flex'
            justifyContent='center'
            alignItems='center'
            minHeight='200px'
          >
            <CircularProgress />
            <Typography variant='body1' sx={{ ml: 2 }}>
              Loading offices...
            </Typography>
          </Box>
        </Wrapper>
      </>
    )
  }

  return (
    <>
      <AppBarR />
      <Wrapper>
        <Box sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>
              <BusinessIcon fontSize='small' />
            </Avatar>
            <Typography component='h1' variant='h6' sx={{ fontWeight: 600 }}>
              Office Management
            </Typography>
          </Box>

          <Typography variant='body1' color='text.secondary' sx={{ mb: 3 }}>
            Manage your offices, desks, and team members
          </Typography>

          {/* Office Management List */}
          {error ? (
            <Alert severity='error' sx={{ mt: 2 }}>
              {error}
            </Alert>
          ) : (
            <OfficeManagementList
              offices={officesWithRoles}
              loading={loading}
            />
          )}
        </Box>
      </Wrapper>
    </>
  )
}

export default Offices
